import { DESTINATION_CITY_BROWSE_SLA_MILLISECONDS } from '@eventbrite/discover-utils';
import { DEFAULT_DESTINATION_EXPANSIONS } from '@eventbrite/event-renderer';
import { sdkRequest } from '@eventbrite/http';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { NEARBY_CITIES } from '../../../../constants/constants';
import { Bucket, BucketsContent, PaginatedBucketData } from '../../../../types';
import { BrowseDataContext } from '../../context';
import { transformBuckets } from './utils';
const fetchBrowseBuckets = ({ queryKey, pageParam }: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, placeId, isOnline] = queryKey;
    const { excludedBuckets } = pageParam;

    const controller = new AbortController();
    const id = setTimeout(() => {
        controller.abort();
    }, DESTINATION_CITY_BROWSE_SLA_MILLISECONDS);

    return sdkRequest('/api/v3/destination/city-browse/', {
        method: 'POST',
        signal: controller.signal,
        body: JSON.stringify({
            place_id: placeId,
            online_events_only: isOnline,
            buckets_to_exclude: excludedBuckets,
            'expand.destination_event': DEFAULT_DESTINATION_EXPANSIONS,
        }),
    })
        .then(
            async (res: {
                buckets: BucketsContent;
                excluded_buckets: BucketsContent;
                more_buckets: BucketsContent;
            }) => {
                const waitForBuckets = async () => {
                    return new Promise((resolve) => {
                        const checkBuckets = () => {
                            if (res.buckets && res.buckets.length > 0) {
                                resolve(res.buckets);
                            } else {
                                requestAnimationFrame(checkBuckets);
                            }
                        };
                        checkBuckets();
                    });
                };

                await waitForBuckets();

                const transformedBuckets = transformBuckets({
                    buckets: res.buckets,
                    expanded: true,
                });

                return {
                    buckets: transformedBuckets,
                    excludedBuckets: res.excluded_buckets,
                    moreBuckets: res.more_buckets,
                };
            },
        )
        .catch((error: any) => {
            console.error(error.response);
            throw error.response;
        })
        .finally(() => {
            clearTimeout(id);
        });
};

// Function to fetch buckets for cityPlaceId
const fetchNearbyPlaceBucket = async (
    cityPlaceId: string,
    isOnline: boolean,
) => {
    const controller = new AbortController();
    const id = setTimeout(() => {
        controller.abort();
    }, DESTINATION_CITY_BROWSE_SLA_MILLISECONDS);
    let response = { buckets: [] };
    try {
        response = await sdkRequest('/api/v3/destination/city-browse/', {
            method: 'POST',
            signal: controller.signal,
            body: JSON.stringify({
                place_id: cityPlaceId,
                online_events_only: isOnline,
                'expand.destination_event': DEFAULT_DESTINATION_EXPANSIONS,
            }),
        });
    } catch (e) {
        console.error(e.response);
        throw e.response;
    } finally {
        clearTimeout(id);
    }
    const nearbyPlaceBucket = response.buckets.find(
        (bucket: any) =>
            bucket.template === 'nearby_place' &&
            bucket.key === 'nearby_cities',
    );
    return nearbyPlaceBucket ? [nearbyPlaceBucket] : [];
};

type BrowseDataLayerData = {
    pages: PaginatedBucketData[];
    pageParams: any[];
};

/** Keeps at most one nearby cities bucket across all pages (the last one). */
function deduplicateNearbyBuckets(
    pages: PaginatedBucketData[],
): PaginatedBucketData[] {
    const flatBuckets = pages.flatMap((page) => page.buckets);
    const lastNearbyIndex = flatBuckets.reduce<number>(
        (last, bucket, index) => (bucket.key === NEARBY_CITIES ? index : last),
        -1,
    );
    let flatIndex = 0;
    return pages.map((page) => ({
        ...page,
        buckets: page.buckets.filter((bucket) => {
            const i = flatIndex++;
            return bucket.key !== NEARBY_CITIES || i === lastNearbyIndex;
        }),
    }));
}

export const MainContentDataLayer = (props: {
    buckets: BucketsContent;
    excludedBuckets?: BucketsContent;
    moreBuckets?: BucketsContent;
    children?: React.ReactNode;
    placeId?: string | null;
    cityPlaceId?: string | null;
    isOnline?: boolean;
}) => {
    const { children, placeId, cityPlaceId, isOnline } = props;
    const queryClient = useQueryClient();
    const queryKey = useMemo(
        () => ['browse-buckets', placeId, isOnline],
        [placeId, isOnline],
    );
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError } =
        useInfiniteQuery(queryKey, fetchBrowseBuckets as any, {
            getNextPageParam: (prevPage) => {
                if (prevPage.moreBuckets) {
                    return {
                        excludedBuckets: prevPage.excludedBuckets,
                    };
                }
            },
            initialData: {
                pages: [
                    {
                        buckets: transformBuckets({
                            buckets: props.buckets,
                            expanded: false,
                        }),
                        excludedBuckets: props.excludedBuckets,
                        moreBuckets: props.moreBuckets,
                    },
                ],
                pageParams: [{ excludedBuckets: props.excludedBuckets }],
            },
        });

    const removeEvent = (bucketKey: string, eventId: string) => {
        queryClient.setQueryData(queryKey, (oldData?: BrowseDataLayerData) => {
            if (!oldData) return;

            const newBucketPages = oldData.pages.map(
                ({ buckets, ...otherData }) => {
                    const newBuckets = buckets.map((bucket: any) => {
                        if (bucket.key === bucketKey) {
                            return {
                                ...bucket,
                                content: bucket.content.filter(
                                    (event: any) => event.id !== eventId,
                                ),
                            };
                        }
                        return bucket;
                    });
                    return {
                        buckets: newBuckets,
                        ...otherData,
                    };
                },
            );

            return {
                ...oldData,
                pages: newBucketPages,
            };
        });
    };

    useEffect(() => {
        const fetchNearbyBucket = async () => {
            if (cityPlaceId) {
                const nearbyBucket = await fetchNearbyPlaceBucket(
                    cityPlaceId,
                    isOnline ?? false,
                );
                if (nearbyBucket.length > 0) {
                    const transformedNearbyBuckets = transformBuckets({
                        buckets: nearbyBucket,
                        expanded: true,
                    });

                    queryClient.setQueryData(
                        queryKey,
                        (oldData?: BrowseDataLayerData) => {
                            if (!oldData) return oldData;
                            const updatedPages = oldData.pages.map((page) => ({
                                ...page,
                                buckets: [
                                    ...page.buckets,
                                    ...transformedNearbyBuckets,
                                ] as Bucket[],
                            }));
                            return {
                                ...oldData,
                                pages: deduplicateNearbyBuckets(updatedPages),
                            };
                        },
                    );
                }
            }
        };

        fetchNearbyBucket();
    }, [cityPlaceId, isOnline, queryClient, queryKey]);

    useEffect(() => {
        const fetchAllPages = async () => {
            await fetchNextPage();
        };
        if (!isFetchingNextPage && hasNextPage && !isOnline && !isError) {
            fetchAllPages();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isOnline, isError]);

    return (
        <BrowseDataContext.Provider
            value={{
                fetchNextPage,
                hasNextPage,
                removeEvent,
                buckets: data?.pages.reduce(
                    (memo: any, page) => [...memo, ...page.buckets],
                    [],
                ),
            }}
        >
            {children}
        </BrowseDataContext.Provider>
    );
};
